import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

// Components
import Typography from '../../../../../../components/Typography';
import Button from '../../../../../../components/CustomButton';

// Styles
import viewsStyles from '../views.module.scss';

// Context
import { useDataSettingsContext } from '../../context';

// Utils
import useCsrfToken from '../../../../../../utils/hooks/csfrToken';
import { userSettingsUrl } from '../../../../../../utils/urls';

const ViewAccountDeletion: React.FC = () => {
  const { t } = useTranslation();
  // Utils
  const csrfToken = useCsrfToken();

  // Context
  const { onClose, user, onSetHeaderTitle } = useDataSettingsContext();

  // States
  const [isLoading, setIsLoading] = useState<boolean>(false);

  // Effects
  useEffect(() => {
    onSetHeaderTitle(t('profile.settings.data.account_deletion.title'));

    return () => {
      onSetHeaderTitle('');
    };
  }, [onSetHeaderTitle, t]);

  // Handlers
  const handleDeleteAccount = useCallback(async () => {
    setIsLoading(true);
    
    try {
      const options = {
        method: 'DELETE',
        headers: {
          'X-CSRF-Token': csrfToken,
          'Accept': 'application/json',
        },
      };

      const response = await fetch(userSettingsUrl(), options);
      
      if (!response.ok) {
        throw new Error(`Failed to delete account: ${response.status} ${response.statusText}`);
      }

      const data = await response.json();
      
      if (data.success && data.redirect_url) {
        Turbo.visit(data.redirect_url);
      }
    } catch (error) {
      console.error('Error deleting account:', error);
      setIsLoading(false);
    }
  }, [csrfToken, t]);

  return (
    <div className={viewsStyles.row_data}>
      <Typography variant="p" className={viewsStyles.row_data_text}>
        {t('profile.settings.data.account_deletion.confirmation')}
      </Typography>

      <Typography variant="p" className={viewsStyles.row_data_text}>
        {t('profile.settings.data.account_deletion.action_warning')}
      </Typography>

      <div className={viewsStyles.actions}>
        <Button
          variant='secondary'
          classes={{
            root: viewsStyles.actions_button,
          }}
          disabled={isLoading}
          onClick={() => onClose()}
        >
          {t('actions.skip')}
        </Button>

        <Button
          variant='primary'
          classes={{
            root: viewsStyles.actions_button,
          }}
          onClick={handleDeleteAccount}
          disabled={isLoading}
        >
          {t('actions.confirm')}
        </Button>
      </div>
    </div>
  );
};

export default ViewAccountDeletion;
